(() => {
  if (customElements.get('caseora-category-control')) return;
  class CategoryControl extends HTMLElement {
    connectedCallback() {
      this.facet = this.closest('product-facet');
      this.abort = new AbortController();
      this.cache = new Map();
      this.sequence = 0;
      this.boot();
    }
    disconnectedCallback() {
      this.sequence++;
      this.abort?.abort();
      this.request?.abort();
    }
    async boot() {
      try {
        this.classifier = await import(this.dataset.module);
        if (!this.isConnected) return;
        const signal = this.abort.signal;
        const url = new URL(location.href);
        const requested = url.searchParams.getAll('caseora_category');
        for (const input of this.querySelectorAll('input[type=checkbox]')) {
          input.checked = requested.includes(input.value);
          input.disabled = false;
        }
        this.addEventListener('change', () => this.apply(1), {signal});
        this.querySelector('[data-category-clear]').addEventListener('click', () => this.clear(), {signal});
        this.facet.addEventListener('facet:criteria-changed', event => {
          if (!this.selected().length) return;
          event.stopImmediatePropagation();
          event.preventDefault();
          this.apply(1, new URL(event.detail.url, location.href));
        }, {capture:true, signal});
        this.facet.addEventListener('click', event => {
          const target = event.target.closest('[data-category-page]');
          if (!target) return;
          event.preventDefault();
          this.apply(Number(target.dataset.categoryPage));
        }, {signal});
        if (requested.length) await this.apply(Number(url.searchParams.get('category_page')) || 1);
      } catch (error) {
        this.report(error);
      }
    }
    selected() {
      return [...this.querySelectorAll('input:checked')].map(input => input.value);
    }
    report(error) {
      if (error.name === 'AbortError') return;
      const status = this.querySelector('[data-category-status]');
      status.textContent = this.dataset.error;
      status.hidden = false;
      console.error('Category filter:', error);
    }
    clear() {
      this.sequence++;
      this.request?.abort();
      for (const input of this.querySelectorAll('input')) input.checked = false;
      const url = new URL(location.href);
      url.searchParams.delete('caseora_category');
      url.searchParams.delete('category_page');
      url.searchParams.delete('page');
      this.dispatchEvent(new CustomEvent('facet:criteria-changed', {bubbles:true, detail:{url:url.href}}));
    }
    async catalogue(sort) {
      if (this.cache.has(sort)) return this.cache.get(sort);
      this.request?.abort();
      this.request = new AbortController();
      const signal = this.request.signal;
      const start = new URL(this.dataset.catalogue, location.origin);
      start.searchParams.set('section_id', 'caseora-category-data');
      start.searchParams.set('sort_by', sort);
      const previewId = new URL(location.href).searchParams.get('preview_theme_id');
      if (previewId) start.searchParams.set('preview_theme_id', previewId);
      const records = new Map();
      let url = start;
      let expected = null;
      const visited = new Set();
      while (url) {
        if (url.origin !== location.origin || visited.has(url.href)) throw new Error('Invalid catalogue page');
        visited.add(url.href);
        const response = await fetch(url, {signal});
        if (!response.ok) throw new Error('Catalogue unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const feed = doc.querySelector('[data-category-feed]');
        if (!feed) throw new Error('Missing catalogue section');
        expected ??= Number(feed.dataset.total);
        for (const entry of feed.querySelectorAll('[data-category-entry]')) {
          const groups = this.classifier.productSeries(JSON.parse(entry.dataset.memberships));
          records.set(entry.dataset.handle, {groups, card:entry.querySelector('product-item')});
        }
        if (feed.dataset.next) {
          url = new URL(feed.dataset.next, location.origin);
          url.searchParams.set('section_id', 'caseora-category-data');
          url.searchParams.set('sort_by', sort);
          if (previewId) url.searchParams.set('preview_theme_id', previewId);
        } else url = null;
      }
      if (records.size !== expected || [...records.values()].some(record => !record.card)) throw new Error('Incomplete catalogue');
      const result = [...records.values()];
      if (this.cache.size >= 2) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(sort, result);
      return result;
    }
    async apply(page = 1, url = new URL(location.href)) {
      const selected = this.selected();
      if (!selected.length) { this.clear(); return; }
      const sequence = ++this.sequence;
      const main = this.facet.querySelector('#facet-main');
      const status = this.querySelector('[data-category-status]');
      const sort = url.searchParams.get('sort_by') || this.dataset.sort;
      main.setAttribute('aria-busy', 'true');
      status.textContent = this.dataset.loading;
      status.hidden = false;
      this.querySelector('[data-category-clear]').hidden = false;
      try {
        const records = await this.catalogue(sort);
        if (sequence !== this.sequence || !this.isConnected) return;
        const matches = records.filter(record => record.groups.some(group => selected.includes(group)));
        const size = Number(this.dataset.pageSize);
        const pages = Math.max(1, Math.ceil(matches.length / size));
        page = Math.max(1, Math.min(page, pages));
        let grid = main.querySelector('.product-list__inner');
        if (!grid) {
          const list = document.createElement('product-list');
          list.className = 'product-facet__product-list product-list anchor';
          grid = document.createElement('div');
          grid.className = 'product-list__inner';
          list.append(grid);
          main.append(list);
          main.querySelector('.empty-state')?.remove();
        }
        const fragment = document.createDocumentFragment();
        for (const record of matches.slice((page - 1) * size, page * size)) fragment.append(record.card.cloneNode(true));
        grid.replaceChildren(fragment);
        for (const pagination of main.querySelectorAll('page-pagination')) pagination.hidden = true;
        main.querySelector('[data-category-pagination]')?.remove();
        const nav = document.createElement('nav');
        nav.dataset.categoryPagination = '';
        nav.className = 'pagination__nav';
        nav.setAttribute('aria-label', this.dataset.pagination);
        for (let index = 1; index <= pages; index++) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pagination__nav-item heading heading--small';
          button.textContent = String(index);
          button.dataset.categoryPage = String(index);
          if (index === page) button.setAttribute('aria-current', 'page');
          nav.append(button);
        }
        if (pages > 1) main.querySelector('product-list').after(nav);
        status.textContent = `${matches.length} ${this.dataset.products}`;
        const counter = main.querySelector('.product-facet__meta-bar-item--count');
        if (counter) counter.textContent = status.textContent;
        for (const input of this.querySelectorAll('input')) {
          const count = records.filter(record => record.groups.includes(input.value)).length;
          input.closest('label').querySelector('[data-category-count]').textContent = `(${count})`;
        }
        const sortInput = main.querySelector(`[name="sort_by"][value="${CSS.escape(sort)}"]`);
        if (sortInput) sortInput.checked = true;
        const sortLabel = sortInput?.closest('label')?.querySelector('.popover__choice-label');
        const selectedLabel = main.querySelector('#sort-by-selected-value');
        if (sortLabel && selectedLabel) selectedLabel.textContent = sortLabel.textContent;
        url.searchParams.delete('caseora_category');
        url.searchParams.delete('page');
        selected.forEach(key => url.searchParams.append('caseora_category', key));
        url.searchParams.set('category_page', String(page));
        history.replaceState(history.state, '', url);
      } catch (error) {
        if (sequence === this.sequence) this.report(error);
      } finally {
        if (sequence === this.sequence) main.setAttribute('aria-busy', 'false');
      }
    }
  }
  customElements.define('caseora-category-control', CategoryControl);
})();
